import Document from '@components/Document';
import DocumentsTree from '@components/DocumentsTree';
import { Grid, CenterColumn, LeftColumn, RightColumn } from '@components/GridLayout';
import HeaderMobileLogin from '@components/Header/components/HeaderMobileLogin';
import { useAppDispatch, useAppSelector } from '@hooks/useStore';
import { useGetDocumentsQuery, useGetDocumentTreeQuery, useGetNamespaceQuery } from '@redux/api/adminApi';
import { setNamespace, setDocumentName } from '@redux/slices/document';
import { setMenuOpen } from '@redux/slices/global';
import { Loader } from '@sberlab/core-components';
import { IRootSlice } from '@typings/rootSlice';
import { initTop100Popular } from '@utils/top100Counter';
import React, { useCallback, useEffect, useRef } from 'react';
import DocumentTitle from 'react-document-title';
import { useLocation, useNavigate, useParams } from 'react-router';

const Namespace: React.FC = () => {
    const mounted = useRef(null);
    const { pathname } = useLocation();
    const params = useParams();
    const namespace = params.namespace;
    const document = params.document;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const documentTree = useAppSelector((state) => state.documentSlice.tree)[namespace] || null;
    const menuOpen = useAppSelector((state: IRootSlice) => state.globalSlice.menuOpen);

    useEffect(() => {
        dispatch(setNamespace(namespace));
    }, [namespace]);

    useEffect(() => {
        dispatch(setDocumentName(document));
    }, [document]);

    const sendPageView = useCallback(() => {
        window.popularCounter?.trackPageview();
    }, []);

    // Инициализируем счетчики при маунте
    // Отправляем PV только при смене pathname
    useEffect(() => {
        if (!mounted.current) {
            initTop100Popular();
            mounted.current = true;
        } else {
            sendPageView();
        }
    }, [pathname]);

    // Редирект на первый документ если заходим на страничку /documentation/namespace
    useEffect(() => {
        if (!document && documentTree && Object.keys(documentTree)?.length) {
            const sortedTree = Object.keys(documentTree)
                .filter((key) => documentTree[key].parent_document_id === null)
                .sort((a, b) => documentTree[a].position - documentTree[b].position);
            navigate(sortedTree[0]);
        }
    });

    const { data: namespaceData } = useGetNamespaceQuery(namespace);
    const documentTitle = namespaceData?.title || 'Справка';
    const namespaceId = namespaceData?.id;
    const documentData = documentTree && documentTree[document];
    const isDocumentDataExist = !!documentData;

    const { isFetching: isDocumentsLoading } = useGetDocumentsQuery(
        { namespaceId, level: 1 },
        { skip: !namespaceId || !!document || namespaceData?.title_for_url !== namespace },
    );

    const { isFetching: isDocTreeLoading } = useGetDocumentTreeQuery(document, {
        skip: !document || isDocumentDataExist,
    });

    const closeMenu = () => dispatch(setMenuOpen(false));

    const handleClickMenuItem = (isClickOnIcon: boolean) => {
        if (!isClickOnIcon && window.matchMedia('(max-width: 1200px)').matches) {
            closeMenu();
        }
    };

    // prettier-ignore
    return (
        <DocumentTitle title={documentTitle}>
            <Loader loading={isDocumentsLoading || isDocTreeLoading}>
                <Grid>
                    <LeftColumn menuOpen={menuOpen} closeMenu={closeMenu}>
                        <DocumentsTree items={documentTree} onClickItem={handleClickMenuItem} />
                    </LeftColumn>
                    <CenterColumn>
                        {document && documentData ? (
                            <Document id={documentData?.document_id} documentTree={documentTree} />
                        ) : null}
                    </CenterColumn>
                    <RightColumn>
                        <HeaderMobileLogin />
                    </RightColumn>
                </Grid>
            </Loader>
        </DocumentTitle>
    );
};

export default Namespace;
